import React from 'react';

/**
 * Small, dependency-free SVG miniature of a template's layout.
 *
 * Renders each widget as a shape matching its visual type on a 12-col grid,
 * mirroring the actual builder canvas. Used in the +New Insight popover as
 * a hover/focus preview so users can see a template's visual intent before
 * committing to it.
 */
const CELL_W = 8;   // px per grid col
const CELL_H = 10;  // px per grid row

function widgetTint(color, alpha = 0.35) {
  // color is #rrggbb -> return rgba string
  if (!color || !color.startsWith('#')) return `rgba(148,163,184,${alpha})`;
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function WidgetShape({ w, color }) {
  const px = w.x * CELL_W;
  const py = w.y * CELL_H;
  const pw = w.w * CELL_W;
  const ph = w.h * CELL_H;
  const fill = widgetTint(color, 0.28);
  const stroke = widgetTint(color, 0.85);

  // Type-specific mini-glyph inside the shape
  const cx = px + pw / 2;
  const cy = py + ph / 2;
  const inner = { stroke: widgetTint(color, 0.9), strokeWidth: 0.9, fill: 'none' };

  return (
    <g>
      <rect x={px + 0.5} y={py + 0.5} width={pw - 1} height={ph - 1} rx={1.5}
            fill={fill} stroke={stroke} strokeWidth={0.6} />
      {w.vizType === 'kpi' && (
        <g>
          <rect x={px + 2} y={py + 2} width={pw - 4} height={2} rx={1} fill={widgetTint(color, 0.9)} />
          <rect x={px + 2} y={py + ph - 6} width={pw / 2} height={3} rx={0.5} fill={widgetTint(color, 0.7)} />
        </g>
      )}
      {w.vizType === 'line_chart' && (
        <polyline
          points={`${px + 2},${py + ph - 3} ${px + pw * 0.25},${py + ph * 0.5} ${px + pw * 0.5},${py + ph * 0.7} ${px + pw * 0.75},${py + ph * 0.3} ${px + pw - 2},${py + ph * 0.4}`}
          {...inner}
        />
      )}
      {w.vizType === 'bar_chart' && (
        <g fill={widgetTint(color, 0.85)}>
          {[0, 1, 2, 3, 4].map(i => {
            const bw = (pw - 6) / 5 - 1;
            const bx = px + 3 + i * ((pw - 6) / 5);
            const h = 2 + ((i * 37) % (ph - 6));
            return <rect key={i} x={bx} y={py + ph - 3 - h} width={bw} height={h} rx={0.5} />;
          })}
        </g>
      )}
      {w.vizType === 'pie_chart' && (
        <g>
          <circle cx={cx} cy={cy} r={Math.min(pw, ph) / 3.2} {...inner} />
          <path d={`M ${cx} ${cy} L ${cx + Math.min(pw, ph) / 3.2} ${cy} A ${Math.min(pw, ph) / 3.2} ${Math.min(pw, ph) / 3.2} 0 0 1 ${cx} ${cy - Math.min(pw, ph) / 3.2} Z`}
                fill={widgetTint(color, 0.7)} />
        </g>
      )}
      {w.vizType === 'table' && (
        <g stroke={widgetTint(color, 0.7)} strokeWidth={0.4}>
          {[0.2, 0.4, 0.6, 0.8].map((f, i) => (
            <line key={i} x1={px + 2} y1={py + ph * f} x2={px + pw - 2} y2={py + ph * f} />
          ))}
          <line x1={px + pw / 2} y1={py + 1} x2={px + pw / 2} y2={py + ph - 1} />
        </g>
      )}
      {w.vizType === 'area_chart' && (
        <path
          d={`M ${px + 2},${py + ph - 2} L ${px + 2},${py + ph * 0.5} L ${px + pw * 0.4},${py + ph * 0.35} L ${px + pw * 0.7},${py + ph * 0.55} L ${px + pw - 2},${py + ph * 0.3} L ${px + pw - 2},${py + ph - 2} Z`}
          fill={widgetTint(color, 0.55)} stroke={widgetTint(color, 0.9)} strokeWidth={0.6}
        />
      )}
    </g>
  );
}

export default function TemplateThumbnail({ template, width = 176, className = '' }) {
  if (!template || !template.layout || !Array.isArray(template.layout.widgets)) return null;
  const widgets = template.layout.widgets;
  // Figure out max extents so we scale cleanly
  const maxCols = 12;
  const maxRows = Math.max(6, ...widgets.map(w => (w.y || 0) + (w.h || 1)));
  const vbW = maxCols * CELL_W;
  const vbH = maxRows * CELL_H;
  const height = Math.round((vbH / vbW) * width);
  return (
    <div
      className={`rounded-md border border-border/40 bg-background/60 p-1 shadow-sm ${className}`}
      style={{ width }}
      data-testid={`template-thumbnail-${template.id}`}
    >
      <svg viewBox={`0 0 ${vbW} ${vbH}`} width={width - 8} height={height} aria-hidden="true">
        <rect x={0} y={0} width={vbW} height={vbH} fill="transparent" />
        {widgets.map((w, i) => (
          <WidgetShape key={i} w={w} color={template.color} />
        ))}
      </svg>
      <div className="mt-1 flex items-center gap-1 px-0.5">
        <span
          className="inline-block w-1.5 h-1.5 rounded-sm shrink-0"
          style={{ background: template.color }}
        />
        <span className="text-[9px] font-medium truncate">{template.name}</span>
      </div>
    </div>
  );
}
